import MessageBanner from '@/components/Alerts/Toaster.vue'
import { useToast } from 'vue-toastification'

/*
	Supporting class for Toaster.vue

	Library: https://github.com/Maronato/vue-toastification
	Sandbox: https://vue-toastification.maronato.dev/

	Usage:
		import { Banner, Toaster } from '@/models/utils/Toaster.js';

		const banner = new Banner();
		const toaster = new Toaster();

	Notes:
		Toasts have a default timeout of 4 seconds
		Pass options as optional second parameter

	Examples:
		toaster.error("Oh dear, you broke it. Go stand in the corner.")
		toaster.success("Aww, well done you.", { timeout: 8000} )

		banner.highWarning("You've done something very, very naughty!")
		banner.info("You should know this, but I am telling you again.")
		banner.error("Oh dear, you broke it. Go stand in the corner.")
		banner.warning("You've done something you shouldn't have.")
		banner.success("Aww, well done you.")

 */


const toast = useToast();


/**
 * @type {Class} Banner
 */
export class Banner
{

	/**
	 * Returns the default options for a banner
	 * @param {string | null} [p_toast_class_name] Optional class name to add to toast
	 * @returns {ToastOptions} Default options
	 */
	defaultOptions(p_toast_class_name)
	{
		let toastClassName = 'banner';
		if (p_toast_class_name)
		{
			toastClassName = ['banner', p_toast_class_name];
		}
		return {
			"position": "top-center",
			"timeout": false,
			"icon": false,
			"draggable": false,
			"closeOnClick": false,
			// For the toast body when using strings or a custom component as content
			"toastClassName": toastClassName
		}
	}

	/**
	 * Clears out any toasts and banners
	 */
	clear()
	{
		toast.clear();
	}

	/**
	 * Shows an error banner
	 * @param {string | Array<string>} p_text Message to show in banner
	 * @param {string} [p_id] Id to give the banner. Used to prevent duplicates of the unauthorized error.
	 */
	error(p_text, p_id)
	{
		if (!p_text || p_text.length === 0)
		{
			p_text = "Unknown error has occurred.";
			console.error("Toaster: No error message provided.");
		}

		if (Array.isArray(p_text))
		{
			p_text = p_text.join('<br/>')
		}

		const options = this.defaultOptions();

		if (p_id && p_id.length > 0)
		{
			options["id"] = p_id;
		}

		toast.error(
			{
				component: MessageBanner,
				props:  {
					type: 'error',
					toastType: 'banner',
					text: p_text,
				},
				listeners: {
					//eventName: eventHandler  // Optional
				}
			},
			options
		);
	}

	/**
	 * Shows an alternate 'high' warning banner
	 * @param {string} p_text Message to show in banner
	 */
	highWarning(p_text)
	{
		if (!p_text || p_text.length === 0) p_text = "Unknown warning has occurred.";
		toast.warning(
			{
				component: MessageBanner,
				props:  {
					type: 'high-warning',
					toastType: 'banner',
					text: p_text
				},
				listeners: {
					//eventName: eventHandler  // Optional
				}
			},
			this.defaultOptions("high-warning")
		);
	}

	/**
	 * Shows an information banner
	 * @param {string} p_text Message to show in banner
	 */
	info(p_text)
	{
		if (!p_text || p_text.length === 0)
		{
			console.error("Toaster: No information message provided.");
			return;
		}
		toast.info(
			{
				component: MessageBanner,
				props:  {
					type: 'info',
					toastType: 'banner',
					text: p_text
				},
				listeners: {
					//eventName: eventHandler  // Optional
				}
			},
			this.defaultOptions()
		);
	}

	/**
	 * Shows a success banner
	 * @param {string} p_text Message to show in banner
	 */
	success(p_text)
	{
		if (!p_text || p_text.length === 0)
		{
			console.error("Toaster: No success message provided.");
			return;
		}
		toast.success(
			{
				component: MessageBanner,
				props:  {
					type: 'success',
					toastType: 'banner',
					text: p_text
				},
				listeners: {
					//eventName: eventHandler  // Optional
				}
			},
			this.defaultOptions()
		);
	}

	/**
	 * Shows a warning banner
	 * @param {string} p_text Message to show in banner
	 */
	warning(p_text)
	{
		if (!p_text || p_text.length === 0)
		{
			console.error("Toaster: No warning message provided.");
			return;
		}
		toast.warning(
			{
				component: MessageBanner,
				props:  {
					type: 'warning',
					toastType: 'banner',
					text: p_text
				},
				listeners: {
					//eventName: eventHandler  // Optional
				}
			},
			this.defaultOptions()
		);
	}
}

/**
 * @type {Class} Toaster
 */
export class Toaster
{

	/**
	 * Returns the default options for a banner
	 * @param {string | null} [p_toast_class_name] Optional class name to add to toast
	 * @returns {ToastOptions} Default options
	 */
	defaultOptions(p_toast_class_name)
	{
		let toastClassName = '';
		if (p_toast_class_name)
		{
			toastClassName = [p_toast_class_name];
		}
		return {
			"position": "bottom-center",
			"timeout": 4000,
			"icon": false,
			// For the toast body when using strings or a custom component as content
			"toastClassName": toastClassName
		}
	}

	/**
	 * Clears out any toasts and banners
	 */
	clear()
	{
		toast.clear();
	}

	/**
	 * Shows an error banner
	 * @param {string} p_text Message to show in banner
	 * @param {Object} p_options Message options
	 */
	error(p_text, p_options = {})
	{
		if (!p_text || p_text.length === 0)
		{
			p_text = "Unknown error has occurred.";
			console.error("Toaster: No error message provided.");
		}

		toast.error(
			{
				component: MessageBanner,
				props:  {
					type: 'error',
					toastType: 'toast',
					text: p_text
				}
			},
			{ ...this.defaultOptions(), ...p_options }
		);
	}

	/**
	 * Shows an alternate 'high' warning banner banner
	 * @param {string} p_text Message to show in banner
	 * @param {Object} p_options Message options
	 */
	highWarning(p_text, p_options = {})
	{
		if (!p_text || p_text.length === 0)
		{
			console.error("Toaster: No high warning message provided.");
			return;
		}
		toast.warning(
			{
				component: MessageBanner,
				props:  {
					type: 'high-warning',
					toastType: 'toast',
					text: p_text
				}
			},
			{ ...this.defaultOptions('high-warning'), ...p_options }
		);
	}

	/**
	 * Shows an information banner
	 * @param {string} p_text Message to show in banner
	 * @param {Object} p_options Message options
	 */
	info(p_text, p_options = {})
	{
		if (!p_text || p_text.length === 0)
		{
			console.error("Toaster: No information message provided.");
			return;
		}
		toast.info(
			{
				component: MessageBanner,
				props:  {
					type: 'info',
					toastType: 'toast',
					text: p_text
				},
				listeners: {
					//eventName: eventHandler  // Optional
				}
			},
			{ ...this.defaultOptions(), ...p_options }
		);
	}

	/**
	 * Shows a success banner
	 * @param {string} p_text Message to show in banner
	 * @param {Object} p_options Message options
	 */
	success(p_text, p_options = {})
	{
		if (!p_text || p_text.length === 0)
		{
			console.error("Toaster: No success message provided.");
			return;
		}
		toast.success({
				component: MessageBanner,
				props:  {
					type: 'success',
					toastType: 'toast',
					text: p_text
				},
				listeners: {
					//eventName: eventHandler  // Optional
				}
			},
			{ ...this.defaultOptions(), ...p_options }
		);
	}

	/**
	 * Shows a warning banner
	 * @param {string} p_text Message to show in banner
	 * @param {Object} p_options Message options
	 */
	warning(p_text, p_options = {})
	{
		if (!p_text || p_text.length === 0)
		{
			console.error("Toaster: No warning message provided.");
			return;
		}
		toast.warning({
				component: MessageBanner,
				props:  {
					type: 'warning',
					toastType: 'toast',
					text: p_text
				},
				listeners: {
					//eventName: eventHandler  // Optional
				}
			},
			{ ...this.defaultOptions(), ...p_options }
		);
	}
}